import { useState, useMemo } from "react"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Progress } from "@/components/ui/progress"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import {
  Settings, AlertCircle, Zap, TrendingUp, Target, ArrowUp, ArrowDown,
  RefreshCw, CalendarIcon, Layers, MessageSquare, DollarSign, TriangleAlert, Info
} from "lucide-react"
import { AreaChart, Area, XAxis, YAxis, CartesianGrid } from "recharts"
import { ChartContainer, ChartTooltip } from "@/components/ui/chart"
import { InfoTooltip } from "../shared/InfoTooltip"
import { ExportDialog, ExportButton, type ExportColumn } from "../shared/ExportDialog"
import { format, subDays } from "date-fns"
import { DateRange } from "react-day-picker"
import type { RawMessage } from "../types"

interface OpenAIProject { id: string; name: string; status: string }

interface OpenAIUsageData {
  completions: {
    input_tokens: number
    output_tokens: number
    requests: number
    byModel: Record<string, { input: number; output: number; requests: number }>
  } | null
  costs: { total: number; byLineItem: Record<string, number>; byDay: { date: string; amount: number }[] } | null
  loading: boolean
  error: string | null
  dateRange: { from: Date; to: Date } | null
  projectId: string | null
}

interface AIUsageTabProps {
  openaiAdminKey: string
  onAdminKeyChange: (key: string) => void
  openaiProjects: OpenAIProject[]
  selectedOpenaiProject: string
  onSelectedProjectChange: (projectId: string) => void
  openaiUsage: OpenAIUsageData
  onFetchUsage: () => void
  dateRange: DateRange
  filteredMessages: RawMessage[]
}

const TELERIVET_DEFAULT_RATE = 0.007
const TELERIVET_PLATFORM_FEE_NOTE = "Platform subscription fee not included (contact Telerivet for your plan rate)"

// ── Telerivet Billing Section ─────────────────────────────────────────────────

interface TelerivetBillingSectionProps {
  filteredMessages: RawMessage[]
  combinedCostEstimate: number | null
  aiTotal: number
}

function TelerivetBillingSection({ filteredMessages, combinedCostEstimate, aiTotal }: TelerivetBillingSectionProps) {
  const [perMessageRate, setPerMessageRate] = useState(TELERIVET_DEFAULT_RATE.toString())

  const billing = useMemo(() => {
    const rate = parseFloat(perMessageRate) || TELERIVET_DEFAULT_RATE
    const incoming = filteredMessages.filter(m => m.direction === "incoming").length
    const outgoing = filteredMessages.filter(m => m.direction === "outgoing").length
    const total = filteredMessages.length
    const uniqueContacts = new Set(filteredMessages.map(m => m.contact_id)).size
    return {
      incoming, outgoing, total, rate,
      estimatedCost: outgoing * rate,
      estimatedTotalCost: total * rate,
      costPerContact: uniqueContacts > 0 ? (total * rate) / uniqueContacts : 0,
      uniqueContacts,
    }
  }, [filteredMessages, perMessageRate])

  return (
    <Card className="border-amber-200 bg-amber-50/50">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-amber-600" />
              Telerivet Message Billing Estimate
              <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-100 text-xs font-normal">Estimates Only</Badge>
            </CardTitle>
            <CardDescription className="mt-1">
              Based on message volume in the selected date range. Rates vary by country, channel, and plan.
            </CardDescription>
          </div>
        </div>
        <Alert className="mt-3 border-amber-300 bg-amber-100">
          <TriangleAlert className="h-4 w-4 text-amber-700" />
          <AlertDescription className="text-amber-800 text-sm">
            <strong>These are estimates only.</strong> Actual Telerivet costs depend on your negotiated plan,
            country/carrier rates, channel type (SMS, WhatsApp, Viber, USSD), and message segments.
            Telerivet pricing starts at <strong>$0.007/message</strong> with bring-your-own connectivity —
            direct routes and platform fees are additional. Contact Telerivet for your actual rates.
          </AlertDescription>
        </Alert>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Label className="text-sm font-medium whitespace-nowrap">Per-message rate ($):</Label>
            <Input type="number" min="0" step="0.001" value={perMessageRate} onChange={e => setPerMessageRate(e.target.value)} className="w-28 font-mono text-sm" placeholder="0.007" />
          </div>
          <p className="text-xs text-muted-foreground">Telerivet publishes a minimum of $0.007 for bring-your-own connectivity. Adjust to your actual rate.</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "Total Messages", value: billing.total.toLocaleString(), sub: `${billing.incoming.toLocaleString()} in · ${billing.outgoing.toLocaleString()} out`, tooltip: "All messages (inbound + outbound) in the selected date range" },
            { label: "Outbound Est.", value: `~$${billing.estimatedCost.toFixed(2)}`, sub: `${billing.outgoing.toLocaleString()} × $${billing.rate.toFixed(4)}`, valueClass: "text-amber-700", tooltip: "Estimated cost for outgoing messages only. This is an estimate." },
            { label: "All Messages Est.", value: `~$${billing.estimatedTotalCost.toFixed(2)}`, sub: `${billing.total.toLocaleString()} × $${billing.rate.toFixed(4)}`, valueClass: "text-amber-700", tooltip: "Estimated cost if all messages are billed. This is an estimate." },
            { label: "Est. per Contact", value: `~$${billing.costPerContact.toFixed(4)}`, sub: `across ${billing.uniqueContacts.toLocaleString()} contacts`, valueClass: "text-amber-700", tooltip: "Estimated messaging cost per unique contact. This is an estimate." },
          ].map(({ label, value, sub, valueClass, tooltip }) => (
            <Card key={label} className="border-0 bg-white shadow-xs">
              <CardContent className="pt-4 pb-4">
                <div className="text-xs font-medium text-muted-foreground uppercase mb-1 flex items-center gap-1">{label}<InfoTooltip text={tooltip} /></div>
                <div className={`text-2xl font-bold ${valueClass || ""}`}>{value}</div>
                <div className="text-xs text-muted-foreground mt-1">{sub}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="flex items-start gap-2 text-xs text-muted-foreground bg-white rounded-md p-3 border">
          <Info className="h-3.5 w-3.5 mt-0.5 shrink-0 text-muted-foreground" />
          <span>{TELERIVET_PLATFORM_FEE_NOTE}. Multi-segment SMS messages may be billed per segment.</span>
        </div>
      </CardContent>
    </Card>
  )
}

// ── OpenAI Usage Section ──────────────────────────────────────────────────────

interface OpenAISectionProps {
  openaiAdminKey: string
  onAdminKeyChange: (key: string) => void
  openaiProjects: OpenAIProject[]
  selectedOpenaiProject: string
  onSelectedProjectChange: (projectId: string) => void
  openaiUsage: OpenAIUsageData
  onFetchUsage: () => void
  dateRange: DateRange
  filteredMessagesCount: number
  usageColumns: ExportColumn[]
  usageData: Record<string, any>[]
  exportOpen: boolean
  onExportOpenChange: (open: boolean) => void
}

function OpenAISection({
  openaiAdminKey, onAdminKeyChange, openaiProjects, selectedOpenaiProject, onSelectedProjectChange,
  openaiUsage, onFetchUsage, dateRange, filteredMessagesCount,
  usageColumns, usageData, exportOpen, onExportOpenChange,
}: OpenAISectionProps) {
  return (
    <div>
      <h3 className="text-base font-semibold mb-4 flex items-center gap-2">
        <Zap className="h-4 w-4 text-emerald-600" />OpenAI API Usage
      </h3>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Settings className="h-4 w-4" />
            OpenAI Admin API Key
            <InfoTooltip text="The Usage API requires an Admin API key. This key is NOT stored - it's only kept in memory for this session." />
          </CardTitle>
          <CardDescription>
            Enter your OpenAI Admin API key to fetch usage statistics. Get your admin key from{" "}
            <a href="https://platform.openai.com/settings/organization/admin-keys" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
              platform.openai.com/settings/organization/admin-keys
            </a>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3">
            <div className="flex-1">
              <Input type="password" placeholder="sk-admin-..." value={openaiAdminKey} onChange={e => onAdminKeyChange(e.target.value)} className="font-mono" />
              <p className="text-xs text-muted-foreground mt-1">🔒 This key is not stored and will be cleared when you leave this page</p>
            </div>
            <Button onClick={onFetchUsage} disabled={openaiUsage.loading || !openaiAdminKey.trim()}>
              <RefreshCw className={`h-4 w-4 mr-2 ${openaiUsage.loading ? "animate-spin" : ""}`} />
              {openaiUsage.loading ? "Loading..." : "Fetch Usage"}
            </Button>
          </div>

          {openaiProjects.length > 0 && (
            <div className="flex items-center gap-3 mt-4 pt-4 border-t">
              <Label className="text-sm font-medium whitespace-nowrap">Filter by Project:</Label>
              <Select value={selectedOpenaiProject} onValueChange={onSelectedProjectChange}>
                <SelectTrigger className="w-[300px]"><SelectValue placeholder="All Projects" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Projects</SelectItem>
                  {openaiProjects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Select a project then click "Fetch Usage" to filter</p>
            </div>
          )}
        </CardContent>
      </Card>

      {openaiUsage.error && (
        <Alert variant="destructive" className="mt-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{openaiUsage.error}</AlertDescription>
        </Alert>
      )}

      {!openaiUsage.completions && !openaiUsage.loading && (
        <Card className="mt-4">
          <CardContent className="py-12 text-center">
            <Zap className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No OpenAI Usage Data Yet</h3>
            <p className="text-gray-500 mb-4">
              Enter your OpenAI Admin API key above and click "Fetch Usage" to see API usage statistics
              for the selected date range: <strong>{format(dateRange.from || subDays(new Date(), 29), "MMM d, yyyy")}</strong> to <strong>{format(dateRange.to || new Date(), "MMM d, yyyy")}</strong>
            </p>
          </CardContent>
        </Card>
      )}

      {openaiUsage.completions && (
        <div className="space-y-6 mt-4">
          {openaiUsage.dateRange && (
            <div className="flex items-center gap-4 text-sm text-muted-foreground bg-muted/50 px-4 py-2 rounded-lg">
              <div className="flex items-center gap-2">
                <CalendarIcon className="h-4 w-4" />
                <span><strong className="text-foreground">{format(openaiUsage.dateRange.from, "MMM d, yyyy")}</strong> to <strong className="text-foreground">{format(openaiUsage.dateRange.to, "MMM d, yyyy")}</strong></span>
              </div>
              <div className="flex items-center gap-2 border-l pl-4">
                <Layers className="h-4 w-4" />
                <span>{openaiUsage.projectId ? <>Project: <strong className="text-foreground">{openaiProjects.find(p => p.id === openaiUsage.projectId)?.name || openaiUsage.projectId}</strong></> : "All Projects"}</span>
              </div>
            </div>
          )}

          {/* Cost Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: "Total Spend", value: `$${typeof openaiUsage.costs?.total === "number" ? openaiUsage.costs.total.toFixed(2) : "0.00"}`, gradient: "from-emerald-500 to-emerald-600", icon: <Zap className="h-8 w-8 text-emerald-200" /> },
              { label: "Input Tokens", value: `${((openaiUsage.completions.input_tokens || 0) / 1000000).toFixed(2)}M`, gradient: "from-blue-500 to-blue-600", icon: <ArrowDown className="h-8 w-8 text-blue-200" /> },
              { label: "Output Tokens", value: `${((openaiUsage.completions.output_tokens || 0) / 1000000).toFixed(2)}M`, gradient: "from-purple-500 to-purple-600", icon: <ArrowUp className="h-8 w-8 text-purple-200" /> },
              { label: "API Requests", value: (openaiUsage.completions.requests || 0).toLocaleString(), gradient: "from-orange-500 to-orange-600", icon: <Target className="h-8 w-8 text-orange-200" /> },
            ].map(({ label, value, gradient, icon }) => (
              <Card key={label} className={`bg-linear-to-br ${gradient} text-white border-0`}>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center justify-between">
                    <div><div className="text-xs font-medium uppercase opacity-80">{label}</div><div className="text-3xl font-bold">{value}</div></div>
                    {icon}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Per-Message Efficiency */}
          {filteredMessagesCount > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: "Input Tokens / Msg", value: ((openaiUsage.completions.input_tokens || 0) / filteredMessagesCount).toFixed(0), valueClass: "text-blue-600", icon: <ArrowDown className="h-6 w-6 text-blue-400" />, tooltip: "Average input tokens consumed per Telerivet message during this period" },
                { label: "Output Tokens / Msg", value: ((openaiUsage.completions.output_tokens || 0) / filteredMessagesCount).toFixed(0), valueClass: "text-purple-600", icon: <ArrowUp className="h-6 w-6 text-purple-400" />, tooltip: "Average output tokens generated per Telerivet message during this period" },
                { label: "AI Cost / Message", value: `$${((typeof openaiUsage.costs?.total === "number" ? openaiUsage.costs.total : 0) / filteredMessagesCount).toFixed(4)}`, valueClass: "text-emerald-600", icon: <Zap className="h-6 w-6 text-emerald-400" />, tooltip: "Average OpenAI API cost per Telerivet message during this period" },
                { label: "Messages in Period", value: filteredMessagesCount.toLocaleString(), icon: <MessageSquare className="h-6 w-6 text-gray-400" />, tooltip: "Total Telerivet messages in the selected date range" },
              ].map(({ label, value, valueClass, icon, tooltip }) => (
                <Card key={label}>
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-xs font-medium text-muted-foreground uppercase flex items-center gap-1">{label}<InfoTooltip text={tooltip} /></div>
                        <div className={`text-2xl font-bold ${valueClass || ""}`}>{value}</div>
                      </div>
                      {icon}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Daily Spend Chart */}
          {openaiUsage.costs?.byDay && openaiUsage.costs.byDay.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />Daily OpenAI Spend
                  <InfoTooltip text="OpenAI API costs per day during the selected time period" />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ChartContainer config={{}} className="h-[250px] w-full">
                  <AreaChart data={openaiUsage.costs.byDay} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorSpend" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0.1} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                    <XAxis dataKey="date" tickFormatter={v => format(new Date(v), "MMM d")} tick={{ fontSize: 11, fill: "#6b7280" }} axisLine={false} tickLine={false} />
                    <YAxis tickFormatter={v => `$${typeof v === "number" ? v.toFixed(2) : "0.00"}`} tick={{ fontSize: 11, fill: "#6b7280" }} axisLine={false} tickLine={false} />
                    <ChartTooltip content={({ active, payload }) => {
                      if (active && payload?.length && payload[0]?.payload) {
                        const amount = typeof payload[0].value === "number" ? payload[0].value : 0
                        return (
                          <div className="bg-white border rounded-lg shadow-lg p-3">
                            <p className="text-sm font-medium">{format(new Date(payload[0].payload.date), "MMM d, yyyy")}</p>
                            <p className="text-sm text-emerald-600 font-semibold">${amount.toFixed(2)}</p>
                          </div>
                        )
                      }
                      return null
                    }} />
                    <Area type="monotone" dataKey="amount" stroke="#10b981" strokeWidth={2} fillOpacity={1} fill="url(#colorSpend)" />
                  </AreaChart>
                </ChartContainer>
              </CardContent>
            </Card>
          )}

          {/* Usage by Model + Costs by Category */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader><CardTitle className="text-base">Usage by Model</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {Object.entries(openaiUsage.completions.byModel)
                    .sort((a, b) => (b[1].input + b[1].output) - (a[1].input + a[1].output))
                    .slice(0, 10)
                    .map(([model, usage]) => {
                      const totalTokens = (usage.input || 0) + (usage.output || 0)
                      const maxTokens = Math.max(...Object.values(openaiUsage.completions!.byModel).map(u => (u.input || 0) + (u.output || 0)))
                      const pct = maxTokens > 0 ? (totalTokens / maxTokens) * 100 : 0
                      return (
                        <div key={model}>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="font-medium truncate" title={model}>{model.length > 30 ? model.substring(0, 30) + "..." : model}</span>
                            <span className="text-muted-foreground">{(totalTokens / 1000).toFixed(1)}K tokens</span>
                          </div>
                          <Progress value={pct} className="h-2" />
                          <div className="flex justify-between text-xs text-muted-foreground mt-1">
                            <span>In: {((usage.input || 0) / 1000).toFixed(1)}K</span>
                            <span>Out: {((usage.output || 0) / 1000).toFixed(1)}K</span>
                            <span>{usage.requests || 0} requests</span>
                          </div>
                        </div>
                      )
                    })}
                  {Object.keys(openaiUsage.completions.byModel).length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">No model breakdown available</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">Costs by Category</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {openaiUsage.costs && Object.entries(openaiUsage.costs.byLineItem)
                    .sort((a, b) => b[1] - a[1])
                    .map(([lineItem, amount]) => {
                      const safeTotal = typeof openaiUsage.costs!.total === "number" ? openaiUsage.costs!.total : 0
                      const safeAmount = typeof amount === "number" ? amount : 0
                      const pct = safeTotal > 0 ? (safeAmount / safeTotal) * 100 : 0
                      return (
                        <div key={lineItem}>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="font-medium">{lineItem}</span>
                            <span className="text-emerald-600 font-medium">${(typeof amount === "number" ? amount : 0).toFixed(2)}</span>
                          </div>
                          <Progress value={pct} className="h-2" />
                          <div className="text-xs text-muted-foreground mt-1">{pct.toFixed(1)}% of total spend</div>
                        </div>
                      )
                    })}
                  {(!openaiUsage.costs || Object.keys(openaiUsage.costs.byLineItem).length === 0) && (
                    <p className="text-sm text-muted-foreground text-center py-4">No cost breakdown available</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Detailed Model Usage Table */}
          <Card>
            <CardHeader className="flex-row items-center justify-between">
              <CardTitle className="text-base">Detailed Model Usage</CardTitle>
              {usageData.length > 0 && <ExportButton onClick={() => onExportOpenChange(true)} label="Export Usage" />}
            </CardHeader>
            <ExportDialog open={exportOpen} onOpenChange={onExportOpenChange} title="Export AI Usage Data" description="Export model usage breakdown as CSV" columns={usageColumns} data={usageData} filename="openai_usage" />
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Model</TableHead>
                    <TableHead className="text-right">Input Tokens</TableHead>
                    <TableHead className="text-right">Output Tokens</TableHead>
                    <TableHead className="text-right">Total Tokens</TableHead>
                    <TableHead className="text-right">Requests</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Object.entries(openaiUsage.completions.byModel)
                    .sort((a, b) => (b[1].input + b[1].output) - (a[1].input + a[1].output))
                    .map(([model, usage]) => (
                      <TableRow key={model}>
                        <TableCell className="font-mono text-sm">{model}</TableCell>
                        <TableCell className="text-right">{usage.input.toLocaleString()}</TableCell>
                        <TableCell className="text-right">{usage.output.toLocaleString()}</TableCell>
                        <TableCell className="text-right font-medium">{(usage.input + usage.output).toLocaleString()}</TableCell>
                        <TableCell className="text-right">{usage.requests.toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  {Object.keys(openaiUsage.completions.byModel).length === 0 && (
                    <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground">No usage data available</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}

// ── AIUsageTab ────────────────────────────────────────────────────────────────

export function AIUsageTab({
  openaiAdminKey, onAdminKeyChange, openaiProjects, selectedOpenaiProject, onSelectedProjectChange,
  openaiUsage, onFetchUsage, dateRange, filteredMessages,
}: AIUsageTabProps) {
  const [exportOpen, setExportOpen] = useState(false)

  const { usageColumns, usageData } = useMemo(() => {
    if (!openaiUsage.completions?.byModel) return { usageColumns: [] as ExportColumn[], usageData: [] as Record<string, any>[] }
    const columns: ExportColumn[] = [
      { key: "model", label: "Model" }, { key: "input_tokens", label: "Input Tokens" },
      { key: "output_tokens", label: "Output Tokens" }, { key: "total_tokens", label: "Total Tokens" },
      { key: "requests", label: "Requests" },
    ]
    const data = Object.entries(openaiUsage.completions.byModel).map(([model, usage]) => ({
      model, input_tokens: usage.input, output_tokens: usage.output,
      total_tokens: usage.input + usage.output, requests: usage.requests,
    }))
    return { usageColumns: columns, usageData: data }
  }, [openaiUsage])

  const estimatedTotalCost = useMemo(() => {
    const total = filteredMessages.length
    return total * TELERIVET_DEFAULT_RATE
  }, [filteredMessages])

  const combinedCostEstimate = useMemo(() => {
    const aiCost = typeof openaiUsage.costs?.total === "number" ? openaiUsage.costs.total : null
    if (aiCost === null) return null
    return aiCost + estimatedTotalCost
  }, [openaiUsage.costs, estimatedTotalCost])

  return (
    <div className="space-y-6">
      {/* Combined cost summary */}
      <Card className="border-blue-200 bg-blue-50/50">
        <CardContent className="pt-4 pb-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium text-blue-800 flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                Combined Estimated Spend (AI + Messaging)
                <Badge variant="outline" className="text-blue-700 border-blue-300 bg-blue-100 text-xs font-normal">Estimates Only</Badge>
              </div>
              {combinedCostEstimate !== null ? (
                <>
                  <div className="text-3xl font-bold text-blue-900 mt-1">~${combinedCostEstimate.toFixed(2)}</div>
                  <div className="text-xs text-blue-700 mt-1">
                    OpenAI: ${(openaiUsage.costs?.total ?? 0).toFixed(2)} · Telerivet messages: ~${estimatedTotalCost.toFixed(2)} (all messages) · Platform fees not included
                  </div>
                </>
              ) : (
                <>
                  <div className="text-3xl font-bold text-blue-900 mt-1">~${estimatedTotalCost.toFixed(2)}</div>
                  <div className="text-xs text-blue-700 mt-1">Telerivet messages only · Add OpenAI key below to include AI costs · Platform fees not included</div>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <TelerivetBillingSection
        filteredMessages={filteredMessages}
        combinedCostEstimate={combinedCostEstimate}
        aiTotal={typeof openaiUsage.costs?.total === "number" ? openaiUsage.costs.total : 0}
      />

      <Separator />

      <OpenAISection
        openaiAdminKey={openaiAdminKey} onAdminKeyChange={onAdminKeyChange}
        openaiProjects={openaiProjects} selectedOpenaiProject={selectedOpenaiProject}
        onSelectedProjectChange={onSelectedProjectChange}
        openaiUsage={openaiUsage} onFetchUsage={onFetchUsage}
        dateRange={dateRange} filteredMessagesCount={filteredMessages.length}
        usageColumns={usageColumns} usageData={usageData}
        exportOpen={exportOpen} onExportOpenChange={setExportOpen}
      />
    </div>
  )
}
